document.addEventListener("DOMContentLoaded", () => {
    // Check if complete-the-set script is already loaded
    if (window.CompleteTheSetLoaded) {
      return;
    }

    window.CompleteTheSetLoaded = true;

    /**
     * Complete the Set Functionality
     * Handles accordion behavior, variant selection, and price updates
     */
    class CompleteTheSet {
      constructor(container) {
        this.container = container;
        this.selectedProducts = new Map(); // productId -> variantId
        this.productData = new Map(); // productId -> variants array
        this.originalAddToCartHandler = null;
        this.mainProductPriceCents = 0;
        this.mainProductSize = null;
        this.mainProductSizeDataIndex = null;

        this.init();
      }

      init() {
        this.setupEventListeners();
        this.loadProductData();
        this.interceptAddToCart();
        this.mainProductPriceCents = parseFloat(`${document.querySelector('[data-product-price]')?.innerText || '0' }`.trim().replace(/[^0-9.-]+/g, '')) * 100 || 0;
        const sizeRadio = document.querySelector('[data-handle="size"] input[type="radio"]:checked');
        if (sizeRadio) {
            this.mainProductSize = sizeRadio.value;
            this.mainProductSizeDataIndex = sizeRadio.dataset.index;
        }
        this.updateAddToCartButtonPrice();
        this.initPhotoswipe();
      }

      initPhotoswipe() {
        // Initialize Photoswipe for thumbnails if theme.Photoswipe is available
        if (typeof theme !== 'undefined' && theme.Photoswipe) {
          new theme.Photoswipe(this.container, 'complete-the-set');
        }
      }

      setupEventListeners() {
        // Thumbnail clicks for photoswipe
        this.container.querySelectorAll('.complete-the-set__item-thumbnail').forEach(thumbnail => {
          thumbnail.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        });

        // Checkbox changes
        this.container.querySelectorAll('[data-product-checkbox]').forEach(checkbox => {
          checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
            const item = checkbox.closest('.complete-the-set__item');
            const header = item.querySelector('[data-toggle-accordion]');
            this.handleCheckboxChange(e.target);
            if (header) {
              this.toggleAccordion(header);
            }
          });
        });

        // Variant option changes
        this.container.querySelectorAll('.variant-input__button').forEach(button => {
          button.addEventListener('click', (e) => {
            this.handleVariantChange(e.target);
          });
        });

        document.addEventListener('variant:change',this.handleMainVariantChange.bind(this));
      }

      handleMainVariantChange(evt) {
        const variant = evt.detail.variant;
        const price = variant.price;
        this.mainProductPriceCents = price;
        if (this.mainProductSizeDataIndex) {
            this.mainProductSize = variant[this.mainProductSizeDataIndex];
        }
        this.updateAddToCartButtonPrice();
      }

      loadProductData() {
        this.container.querySelectorAll('.complete-the-set__item').forEach(item => {
          const productId = item.dataset.productId;
          const variantsScript = item.querySelector('[data-variants]');

          if (variantsScript) {
            try {
              const variants = JSON.parse(variantsScript.textContent);
              this.productData.set(productId, variants);

              // Initialize with first available variant
              const firstAvailableVariant = variants.find(v => v.available);
              if (firstAvailableVariant) {
                this.setInitialVariant(item, firstAvailableVariant);
              }

              this.updateAvailability(item);
            } catch (error) {
              console.error('Error parsing variant data:', error);
            }
          }
        });
      }

      setInitialVariant(item, variant) {
        const productId = item.dataset.productId;

        // Set default selections based on first available variant
        variant.options.forEach((optionValue, index) => {
          const optionPosition = index + 1;
          const input = item.querySelector(`input[data-option-position="${optionPosition}"][data-option-value="${optionValue}"]`);
          if (input) {
            input.checked = true;
          }
        });

        // Update price display
        this.updatePriceDisplay(item);
      }

      toggleAccordion(header) {
        const item = header.closest('.complete-the-set__item');
        const content = item.querySelector('[data-accordion-content]');
        const checkbox = item.querySelector('[data-product-checkbox]');

        const isExpanded = checkbox.checked ? false : true;

        if (isExpanded) {
          content.classList.remove('expanded');
        } else {
          content.classList.add('expanded');
        }
      }
      updateAddToCartButtonPrice() {
        const addToCartBtn = this.container.closest('[data-product-blocks]')?.querySelector('[data-add-to-cart]');
        if (!addToCartBtn) return;
        // Get main product price from DOM
        let totalCents = this.mainProductPriceCents;

        // Add selected related products
        for (const [_productId, data] of this.selectedProducts) {
          const variant = data.variant
          if (variant) {
          totalCents += variant.price;
          }
        }

        // Format and update button
        const formatted = this.formatMoney(totalCents);
        const text = addToCartBtn.querySelector('[data-add-to-cart-text]').dataset.addToCartText || 'Add to Cart';
        addToCartBtn.querySelector('[data-add-to-cart-text]').innerText = `${text} – ${formatted}`;
      }

      handleCheckboxChange(checkbox) {
        const productId = checkbox.value;
        const item = checkbox.closest('.complete-the-set__item');
        const content = item.querySelector('[data-accordion-content]');
        const hasVariants = item.dataset.hasVariants === 'true';

        if (checkbox.checked) {
          // For single-variant products, directly select the variant
          if (!hasVariants) {
            const variantId = checkbox.dataset.variantId;
            if (variantId) {
              const variants = this.productData.get(productId);
              const variant = variants?.find(v => v.id === parseInt(variantId));
              if (variant) {
                this.selectedProducts.set(productId, {
                  variant: variant,
                  item: item
                });
              }
            }
          } else {
            // For multi-variant products, expand accordion
            if (content) {
              content.classList.add('expanded');
            }
            // Only add to selection if we have a complete variant
            const selectedVariant = this.getSelectedVariant(item);
            if (selectedVariant) {
              this.selectProduct(item);
            }
          }
        } else {
          if (content) {
            content.classList.remove('expanded');
          }
          this.selectedProducts.delete(productId);

          // Deselect all variant buttons and show "from" price
          item.querySelectorAll('.variant-input__button').forEach(button => {
            button.classList.remove('selected');
          });
          if (hasVariants) {
            this.showFromPrice(item);
          }
        }
        this.updateAddToCartButtonPrice();
      }

      handleVariantChange(button) {
        const item = button.closest('.complete-the-set__item');
        const checkbox = item.querySelector('[data-product-checkbox]');
        const optionPosition = button.dataset.optionPosition;

        // Toggle button selection
        if (button.classList.contains('selected')) {
          // Deselect this button
          button.classList.remove('selected');
        } else {
          // Deselect all other buttons in the same option group
          const sameGroup = item.querySelectorAll(`button[data-option-position="${optionPosition}"]`);
          sameGroup.forEach(btn => btn.classList.remove('selected'));

          // Select this button
          button.classList.add('selected');
        }

        // Check if we have a complete variant selection
        const selectedVariant = this.getSelectedVariant(item);

        if (selectedVariant) {
          // Auto-check the product when we have a valid variant
          if (!checkbox.checked) {
            checkbox.checked = true;
          }
          this.selectProduct(item);
          this.updatePriceDisplay(item);
        } else {
          // Show "from" price if no complete variant selected
          this.showFromPrice(item);

          // Check if any options are selected
          const anySelected = item.querySelectorAll('.variant-input__button.selected').length > 0;
          if (!anySelected && checkbox.checked) {
            // If no options selected and product was checked, uncheck it
            checkbox.checked = false;
            this.handleCheckboxChange(checkbox);
          }
        }
        this.updateAddToCartButtonPrice();
      }

      selectProduct(item) {
        const productId = item.dataset.productId;
        const selectedVariant = this.getSelectedVariant(item);
        if (selectedVariant) {
          this.selectedProducts.set(productId, {
            variant: selectedVariant,
            item
          });
          return true;
        }
        return false;
      }

      getSelectedVariant(item) {
        const productId = item.dataset.productId;
        const variants = this.productData.get(productId);

        if (!variants) return null;

        // Get selected options from buttons
        const selectedOptions = [];
        const selectedButtons = item.querySelectorAll('.variant-input__button.selected');

        if (selectedButtons.length === 0) {
            const sizePositon = this.mainProductSizeDataIndex?.replace('option', '');
            if (sizePositon) {
                const getButton = [...item.querySelectorAll('.variant-input__button')].find(button => {
                    return button.dataset.optionPosition === sizePositon && button.dataset.optionValue === this.mainProductSize;
                });
                if (getButton) {
                    getButton.click();
                    selectedOptions[parseInt(sizePositon) - 1] = this.mainProductSize;
                } else {
                    const firstButton = item.querySelector('.variant-input__button');
                    firstButton.click();
                    selectedOptions[parseInt(sizePositon) - 1] = firstButton.dataset.optionValue;
                }
            }
        } else {
            selectedButtons.forEach(button => {
                const position = parseInt(button.dataset.optionPosition);
                const value = button.dataset.optionValue;
                selectedOptions[position - 1] = value;
            });
        }

        // Get all option groups that are displayed (have multiple values)
        const displayedOptionPositions = [];
        item.querySelectorAll('.variant-input-wrap').forEach(wrap => {
          const firstButton = wrap.querySelector('.variant-input__button');
          if (firstButton) {
            const position = parseInt(firstButton.dataset.optionPosition);
            displayedOptionPositions.push(position);
          }
        });

        // Only require selections for displayed options
        const hasAllRequiredSelections = displayedOptionPositions.every(position => {
          return selectedOptions[position - 1] !== undefined;
        });

        if (!hasAllRequiredSelections) {
          return null; // Incomplete selection for displayed options
        }

        // Find matching variant - for options not displayed, accept any value
        return variants.find(variant => {
          return variant.options.every((option, index) => {
            const position = index + 1;
            // If this option position is displayed, it must match selection
            if (displayedOptionPositions.includes(position)) {
              return selectedOptions[index] === option;
            }

            // If option is not displayed (only has 1 value), accept any value
            return true;
          });
        }) || null;
      }

      updatePriceDisplay(item) {
        const priceDisplay = item.querySelector('[data-price-display]');
        const selectedVariant = this.getSelectedVariant(item);

        if (selectedVariant && priceDisplay) {
          // Format price using Shopify's money format
          const formattedPrice = this.formatMoney(selectedVariant.price);
          priceDisplay.textContent = formattedPrice;
        } else {
          // Show "from" price if no variant selected
          this.showFromPrice(item);
        }
        this.updateAddToCartButtonPrice();
      }

      showFromPrice(item) {
        const priceDisplay = item.querySelector('[data-price-display]');
        const productId = item.dataset.productId;
        const variants = this.productData.get(productId);
        const availableVariants = variants.filter(v => v.available);

        if (availableVariants.length > 0 && priceDisplay) {
          const cheapestPrice = Math.min(...availableVariants.map(v => v.price));
          const formattedPrice = this.formatMoney(cheapestPrice);
          priceDisplay.textContent = `from: ${formattedPrice}`;
        }
      }

      updateAvailability(item) {
        const productId = item.dataset.productId;
        const variants = this.productData.get(productId);

        if (!variants) return;

        // Get currently selected options to filter available combinations
        const selectedOptions = [];
        item.querySelectorAll('.variant-input__button.selected').forEach(button => {
          const position = parseInt(button.dataset.optionPosition);
          const value = button.dataset.optionValue;
          selectedOptions[position - 1] = value;
        });

        // Check availability for each button
        item.querySelectorAll('.variant-input__button').forEach(button => {
          const position = parseInt(button.dataset.optionPosition);
          const value = button.dataset.optionValue;

          // Create a test selection with this button's value
          const testSelection = [...selectedOptions];
          testSelection[position - 1] = value;

          // Check if any variant matches this combination
          const hasAvailableVariants = variants.some(variant => {
            if (!variant.available) return false;

            return variant.options.every((option, index) => {
              const testValue = testSelection[index];

              // If we have a test value for this position, it must match
              if (testValue !== undefined) {
                return testValue === option;
              }

              // If no test value, any option value is acceptable
              return true;
            });
          });

          if (hasAvailableVariants) {
            button.classList.remove('disabled');
            button.disabled = false;
          } else {
            button.classList.add('disabled');
            button.disabled = true;

            // If this button is selected but now unavailable, deselect it
            if (button.classList.contains('selected')) {
              button.classList.remove('selected');
            }
          }
        });
      }

      formatMoney(cents) {
        // Use theme's money formatting if available
        if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
          return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
        }

        // Fallback formatting
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
      }

      interceptAddToCart() {
        // Find the main product form
        const productForm = this.container.closest('[data-product-blocks]')?.querySelector('form[action*="/cart/add"]');
        if (!productForm) return;

        // Override form submission - use capture phase to run before other handlers
        productForm.addEventListener('submit', (e) => {
          // Immediately prevent default and stop propagation if we have selected products
          if (this.selectedProducts.size > 0) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
          }
          this.handleAddToCart(e);
        }, true); // Use capture phase to ensure this runs first
      }

      async handleAddToCart(e) {
        // Only proceed with custom cart logic if we have selected upsells
        if (this.selectedProducts.size === 0) {
          return; // Let normal form submission proceed
        }

        // Event is already prevented in the listener above

        const form = e.target;
        const formData = new FormData(form);

        // Prepare items array - main product + selected upsells
        const items = [];

        // Add main product
        const mainProductData = {
          id: Number(formData.get('id')) ,
          quantity: parseInt(formData.get('quantity') || '1')
        };

        // Add properties if they exist
        const properties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propKey = key.replace('properties[', '').replace(']', '');
            properties[propKey] = value;
          }
        }

        if (Object.keys(properties).length > 0) {
          mainProductData.properties = properties;
        }

        items.push(mainProductData);

        // Add selected upsell products
        for (const [_key, data] of this.selectedProducts) {
          const propertiesElements = data.item?.querySelectorAll('input[name^="properties"]');
          const properties = [...propertiesElements]?.reduce((acum, element) => {
            const key = element.name.replace('properties[', '').replace(']', '').replace('[]','');
            acum[key] = element.value;
            return acum
          }, {});
          items.push({
            id: data.variant.id,
            quantity: 1,
            properties: properties || {}
          });
        }
        try {
          // Show loading state
          const addToCartBtn = form.querySelector('[data-add-to-cart]');
          if (addToCartBtn) {
            addToCartBtn.classList.add('btn--loading');
          }

          // Add all items to cart
          const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items })
          });

          if (response.ok) {
            const result = await response.json();

            // Remove loading state
            if (addToCartBtn) {
              addToCartBtn.classList.remove('btn--loading');
            }

            // Trigger success events similar to theme's AjaxProduct
            const addEvent = new CustomEvent('ajaxProduct:added', {
              detail: {
                product: result,
                addToCartBtn: addToCartBtn
              },
              bubbles: true
            });

            form.dispatchEvent(addEvent);

            // Reset selections
            this.resetSelections();

          } else {
            console.error('Error adding to cart:', error);
            const errorData = await response.json();
            this.handleAddToCartError(errorData, addToCartBtn);
          }
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.handleAddToCartError({ message: 'An error occurred while adding to cart' }, form.querySelector('[data-add-to-cart]'));
      }
    }

    handleAddToCartError(error, addToCartBtn) {
      // Remove loading state
      if (addToCartBtn) {
        addToCartBtn.classList.remove('btn--loading');
      }

      // Trigger error event similar to theme's AjaxProduct
      const errorEvent = new CustomEvent('ajaxProduct:error', {
        detail: {
          errorMessage: error.description || error.message
        },
        bubbles: true
      });

      document.dispatchEvent(errorEvent);
    }

    resetSelections() {
      this.selectedProducts.clear();

      // Uncheck all checkboxes and collapse accordions
      this.container.querySelectorAll('[data-product-checkbox]').forEach(checkbox => {
        checkbox.checked = false;
      });

      this.container.querySelectorAll('[data-accordion-content]').forEach(content => {
        content.classList.remove('expanded');
      });
    }
  }

  // Initialize function
  function initCompleteTheSet() {
    const completeTheSetContainer = document.querySelector('[data-complete-the-set]');

    if (completeTheSetContainer && !completeTheSetContainer.dataset.initialized) {
      completeTheSetContainer.dataset.initialized = 'true';
      new CompleteTheSet(completeTheSetContainer);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCompleteTheSet);
  } else {
    initCompleteTheSet();
  }

  // Also initialize on section reloads (for theme editor)
  document.addEventListener('shopify:section:load', function(e) {
    const completeTheSetContainer = e.target.querySelector('[data-complete-the-set]');

    if (completeTheSetContainer) {
      completeTheSetContainer.dataset.initialized = 'true';
      new CompleteTheSet(completeTheSetContainer);
    }
  });

  // Initialize on page load events that theme might trigger
  document.addEventListener('page:loaded', initCompleteTheSet);
  document.addEventListener('quickview:loaded', initCompleteTheSet);
});
